import sanitizeHtml from 'sanitize-html'

// Parser-based and DOM-independent: the same policy runs during SSG and hydration.
// Existing migrated articles embed YouTube and Vimeo; no other frame origins are allowed.
export function sanitizeArticleHtml(value: unknown): string {
	return sanitizeHtml(typeof value === 'string' ? value : '', {
		allowedTags: [...sanitizeHtml.defaults.allowedTags, 'img', 'figure', 'figcaption', 'iframe', 'video', 'audio', 'source'],
		allowedAttributes: {
			'*': ['class', 'id', 'dir', 'lang', 'title'],
			p: ['style'],
			blockquote: ['style'],
			li: ['style'],
			a: ['href', 'name', 'target', 'rel'],
			img: ['src', 'alt', 'width', 'height', 'loading'],
			iframe: ['src', 'width', 'height', 'title', 'allowfullscreen', 'loading'],
			video: ['src', 'poster', 'controls', 'width', 'height', 'preload'],
			audio: ['src', 'controls', 'preload'],
			source: ['src', 'type'],
			td: ['colspan', 'rowspan'],
			th: ['colspan', 'rowspan', 'scope'],
		},
		allowedSchemes: ['https', 'http', 'mailto', 'tel'],
		allowedSchemesByTag: { iframe: ['https'] },
		allowProtocolRelative: false,
		allowedIframeHostnames: ['www.youtube.com', 'www.youtube-nocookie.com', 'player.vimeo.com'],
		allowedStyles: {
			'*': {
				'padding-left': [/^\d+(?:\.\d+)?(?:px|em|rem)$/],
				'padding-inline-start': [/^\d+(?:\.\d+)?(?:px|em|rem)$/],
			},
		},
		allowIframeRelativeUrls: false,
		transformTags: {
			iframe: (_tagName, attribs) => ({
				tagName: 'iframe',
				attribs: { ...attribs, src: attribs.src?.startsWith('//') ? `https:${attribs.src}` : attribs.src || '' },
			}),
			a: (_tagName, attribs) => {
				const href = attribs.href || ''
				const internal = /^(?:\/|#|https?:\/\/(?:www\.)?thetorah\.com(?:\/|$))/.test(href)
				const next = { ...attribs }
				if (internal) {
					delete next.target
					delete next.rel
				}
				else if (next.target === '_blank') next.rel = 'noopener noreferrer'
				return { tagName: 'a', attribs: next }
			},
		},
	})
}

/** Titles are stored as a single rich-text paragraph. Keep inline markup, drop the wrapper. */
export function sanitizeArticleTitleHtml(value: unknown): string {
	const html = sanitizeArticleHtml(value).trim()
	const wrapped = html.match(/^<p\b[^>]*>([\s\S]*)<\/p>$/i)
	return (wrapped && !/<\/p>/i.test(wrapped[1]) ? wrapped[1] : html).trim()
}

export function articleTitleText(value: unknown): string {
	return sanitizeArticleTitleHtml(value).replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
}

export function sanitizeFootnotes(value: string | Array<{ id: number; text: string }> | undefined) {
	return Array.isArray(value)
		? value.map(note => ({ ...note, text: sanitizeArticleHtml(note.text) }))
		: sanitizeArticleHtml(value)
}

export function articleHtmlFields(record: Record<string, unknown>) {
	return {
		subtitle: sanitizeArticleHtml(record.teaser || record.subtitle),
		imageCaption: sanitizeArticleHtml(record.caption || record.imageCaption),
		intervieweeBio: sanitizeArticleHtml(record.intervieweeBio),
		content: sanitizeArticleHtml(record.content),
	}
}
